/**
 * PATH: packages/email-hub/src/feature/components/RehomeOrgControl.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-REHOMEORG-001
 * PURPOSE: WHERE-lane control to move an email's home organisation.
 * WHY: Filing Integrity P3 — allocate cannot cross orgs; re-home is the
 *      audited mutation that unlocks Bravada (etc.) board pickers.
 *      Multi-company only (hidden for single-org members). If the
 *      Message-ID already exists in the target org, BE returns 409 and
 *      the inbox row stays — operator can copy the sibling id to open
 *      after switching organisation (cross-tenant deep-link not available).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { Building2 } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { AnchoredPopover } from '../../_internal/components/AnchoredPopover'
import { emailApi } from '../lib/api'

export interface RehomeTarget {
  id: number
  name: string
  already_present_email_id?: string
  already_present_organisation_name?: string
}

export interface RehomePreview {
  email_id: string
  from_organisation_id: number
  from_organisation_name: string
  active_allocation_count: number
  pending_allocation_count: number
  member_organisation_count: number
  movable_target_count: number
  eligible_targets: RehomeTarget[]
  can_rehome: boolean
  blockers: string[]
}

export function siblingFromRehomeError(err: unknown): {
  emailId?: string
  organisationName?: string
  organisationId?: number
  message: string
} {
  const fallback =
    'This message is already synced in that organisation. ' +
    'It stays here — switch organisation to open the copy.'
  if (!err || typeof err !== 'object') {
    return { message: err instanceof Error ? err.message : fallback }
  }
  const e = err as {
    message?: string
    code?: string
    status?: number
    details?: Record<string, unknown>
  }
  const details = e.details && typeof e.details === 'object' ? e.details : {}
  const emailId =
    typeof details.existing_email_id === 'string'
      ? details.existing_email_id
      : undefined
  const organisationName =
    typeof details.existing_organisation_name === 'string'
      ? details.existing_organisation_name
      : undefined
  const organisationId =
    typeof details.existing_organisation_id === 'number'
      ? details.existing_organisation_id
      : undefined
  if (e.code === 'rehome_duplicate_message' || e.status === 409) {
    return {
      emailId,
      organisationName,
      organisationId,
      message: e.message || fallback,
    }
  }
  return {
    emailId,
    organisationName,
    organisationId,
    message: (typeof e.message === 'string' && e.message) || fallback,
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', '')
    el.style.position = 'fixed'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(el)
    return ok
  } catch {
    return false
  }
}

export function RehomeOrgControl({
  emailId,
  className,
  onRehomed,
}: {
  emailId: string
  className?: string
  onRehomed?: () => void
}) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<RehomePreview | null>(null)
  const [gateChecked, setGateChecked] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const offerSiblingRecovery = useCallback(
    async (opts: {
      siblingEmailId?: string
      organisationName?: string
      lead?: string
    }) => {
      const orgLabel = opts.organisationName || 'that organisation'
      const lead =
        opts.lead ||
        `Already synced in ${orgLabel}. This inbox row stays.`
      if (opts.siblingEmailId) {
        const copied = await copyText(opts.siblingEmailId)
        toast(
          copied
            ? `${lead} Copied sibling email id — switch to ${orgLabel}, then open/paste it.`
            : `${lead} Switch to ${orgLabel} to open the copy (id ${opts.siblingEmailId}).`,
          'info',
        )
        return
      }
      toast(`${lead} Switch to ${orgLabel} to open the copy.`, 'info')
    },
    [toast],
  )

  const load = useCallback(async (opts?: { quiet?: boolean }) => {
    setLoading(true)
    try {
      const data = await emailApi<RehomePreview>(
        `/api/emails/${emailId}/rehome/`,
      )
      setPreview(data)
      return data
    } catch (err) {
      if (!opts?.quiet) {
        toast(
          `Could not load organisations: ${siblingFromRehomeError(err).message}`,
          'error',
        )
        setOpen(false)
      }
      return null
    } finally {
      setLoading(false)
      setGateChecked(true)
    }
  }, [emailId, toast])

  // Soft prefetch so single-org users never see a useless Move org control.
  useEffect(() => {
    setGateChecked(false)
    setPreview(null)
    void load({ quiet: true })
  }, [load])

  useEffect(() => {
    if (open) void load()
  }, [open, load])

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [containerRef, popoverRef])

  const multiOrg =
    (preview?.member_organisation_count ?? 0) >= 2 ||
    (preview?.eligible_targets.length ?? 0) > 0

  const runRehome = async (targetId: number, targetName: string) => {
    const allocNote =
      (preview?.active_allocation_count ?? 0) > 0
        ? `\n\n${preview!.active_allocation_count} active board placement(s) will be revoked.`
        : ''
    const ok = window.confirm(
      `Move this email's home organisation to ${targetName}?${allocNote}\n\n` +
        `Only for multi-company setups. Your current inbox copy stays until ` +
        `home org actually changes — if ${targetName} already has this message ` +
        `synced, the move is refused and nothing vanishes.`,
    )
    if (!ok) return
    setBusy(true)
    try {
      await emailApi(`/api/emails/${emailId}/rehome/`, {
        method: 'POST',
        body: {
          target_organisation_id: targetId,
          allocation_policy: 'clear_pending',
        },
      })
      toast(
        `Moved to ${targetName}. You can now allocate onto that organisation's boards.`,
        'success',
      )
      setOpen(false)
      onRehomed?.()
    } catch (err) {
      const sib = siblingFromRehomeError(err)
      if (
        (err as { code?: string; status?: number })?.code ===
          'rehome_duplicate_message' ||
        (err as { status?: number })?.status === 409
      ) {
        await offerSiblingRecovery({
          siblingEmailId: sib.emailId,
          organisationName: sib.organisationName || targetName,
          lead: sib.message,
        })
      } else {
        toast(sib.message, 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  if (gateChecked && preview && !multiOrg) {
    return null
  }

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        data-testid="rehome-org-trigger"
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:ring-slate-600 dark:hover:bg-slate-800"
        onClick={() => setOpen((v) => !v)}
      >
        <Building2 className="h-3 w-3" aria-hidden />
        Move org
      </button>
      {open && (
        <AnchoredPopover
          anchorRef={containerRef}
          open={open}
          popoverRef={popoverRef}
          role="menu"
          className="w-64 rounded-md border border-border bg-card p-2 shadow-lg"
        >
          <div data-testid="rehome-org-menu">
            <div className="mb-1 px-1 text-[11px] font-semibold text-foreground">
              Move to organisation
            </div>
            <p className="mb-2 px-1 text-[10px] text-muted-foreground">
              Multi-company only. Changes home org for allocate boards — not CRM
              company, and not a tag. Already-synced targets stay in this inbox;
              click them to copy the sibling id, then switch organisation.
            </p>
            {loading && (
              <div className="px-1 py-2 text-[11px] text-muted-foreground">
                Loading…
              </div>
            )}
            {!loading && preview && preview.blockers.length > 0 && (
              <div className="mb-2 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                {preview.blockers.join('; ')}
              </div>
            )}
            {!loading &&
              preview?.eligible_targets.map((t) => {
                const already = Boolean(t.already_present_email_id)
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={busy}
                    data-testid={
                      already
                        ? `rehome-org-already-${t.id}`
                        : `rehome-org-target-${t.id}`
                    }
                    title={
                      already
                        ? `Already synced in ${t.name} — click to copy sibling email id`
                        : undefined
                    }
                    className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left text-[12px] text-foreground hover:bg-muted disabled:opacity-50"
                    onClick={() => {
                      if (already) {
                        void offerSiblingRecovery({
                          siblingEmailId: t.already_present_email_id,
                          organisationName:
                            t.already_present_organisation_name || t.name,
                        })
                        return
                      }
                      void runRehome(t.id, t.name)
                    }}
                  >
                    <span>{t.name}</span>
                    {already && (
                      <span className="text-[10px] text-muted-foreground">
                        Already synced — click to copy id
                      </span>
                    )}
                  </button>
                )
              })}
            {!loading && preview && preview.eligible_targets.length === 0 && (
              <div className="px-1 py-2 text-[11px] text-muted-foreground">
                No other organisations available.
              </div>
            )}
          </div>
        </AnchoredPopover>
      )}
    </div>
  )
}

export default RehomeOrgControl
